/* eslint-disable consistent-return */
import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { Typeahead } from 'react-bootstrap-typeahead';
import { useTranslation } from 'react-i18next';
import { cloneDeep } from 'lodash';
import { selectNewStyle, selectLibraryStyle, CustomOption } from '../../../Components/helper';
import * as careLabelAction from './store/CARELABEL.action';
import DragDropSort from '../../../Components/DragDropSort/DragDropSort';
import { useCareInstructionState } from './CareLabelProvider/CareUserInstructionStateProvider';
import { toasterMsg } from '../../../Components/Toaster/Toaster';
import CustomTooltip from '../../../Components/Tooltip/CustomTooltip';
import './ProductCareLabel.css';

const CARE_LIBRARY_SEARCH_FIELDS = ['code', 'description'];

/**
 * Tells whether a merged care library cell holds text the import typeahead can match against.
 * @param {*} value Cell value taken from a Code or Description column.
 * @returns {boolean} True when the value is a non-empty string.
 */
const hasSearchableText = (value) => typeof value === 'string' && value.trim() !== '';

function ProductCareImport({ handleDeleteMultiple, handleAddCareLabel, CareAddEditAccess, CareDeleteAccess, isProductArchived }) {
  const { t } = useTranslation('common');
  const params = useParams();
  const devOrderId = params?.devOrderId;
  const dispatch = useDispatch();
  const reducerState = useSelector((redState) => redState);
  const {
    CareLabelProductReducer: { careGroupList, productList, careLibrariesData },
  } = reducerState;

  const { userState, CareLabelstate, setCareLabelState, setUserState } = useCareInstructionState();
  const typeaheadConfig = useRef(null);
  const newtypeaheadConfig = useRef(null);
  const [selectDrop, setSelectDrop] = useState({
    value: 'Library',
    label: 'Library',
  });
  const [state, setState] = useState({
    isFocused: false,
    productList: [],
    selectProductList: null,
    CareLibraryList: [],
    showDragDrop: false,
  });

  const { CareLabelList } = CareLabelstate;
  const findIndex = CareLabelList?.findIndex((item) => item?.tabId === userState?.tabId);
  const codeColumn = CareLabelList?.[findIndex]?.tabColumns?.find((ele) => ele?.name === 'Code')?._id;
  const typeColumn = CareLabelList?.[findIndex]?.tabColumns?.find((ele) => ele?.name === 'Care Type' || ele?.name === 'Disclaimer Type')?._id;
  const options = [
    {
      value: 'Library',
      label: (
        <CustomTooltip placement="right" tooltipText={t('TAKING_DATES.LIBRARYS')}>
          <i className="icon-Library-1 library-icon-css f-24" />
        </CustomTooltip>
      ),
    },
    {
      value: 'Group',
      label: (
        <CustomTooltip placement="right" tooltipText={t('TAKING_DATES.GROUP')}>
          <i className="icon-Product-Group library-icon-css f-24" />
        </CustomTooltip>
      ),
    },
    {
      value: 'Product',
      label: (
        <CustomTooltip placement="right" tooltipText={t('TAKING_DATES.PRODUCT')}>
          <i className="icon-Product library-icon-css f-24" />
        </CustomTooltip>
      ),
    },
  ];

  const placeHolder = () => {
    return selectDrop?.value;
  };

  useEffect(() => {
    if (userState?.tabId) {
      const fetchSuggestions = async () => {
        await dispatch(careLabelAction.fetchCareLibraries(userState?.tabId));
      };
      fetchSuggestions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userState.tabId]);

  useEffect(() => {
    if (careLibrariesData?.columns?.length > 0 && careLibrariesData?.rows?.length > 0) {
      const codeColumnId = careLibrariesData?.columns
        .map((col) => {
          if (col?.name === 'Code') return col?._id;
        })
        .filter((ele) => ele !== undefined);

      const descriptionColumnId = careLibrariesData?.columns
        .map((col) => {
          if (col?.name === 'Description') return col?._id;
        })
        .filter((ele) => ele !== undefined);

      const codeRows = careLibrariesData?.rows?.filter((row) => codeColumnId.includes(row.columnId.toString()));
      const descriptionRows = careLibrariesData?.rows?.filter((row) => descriptionColumnId.includes(row.columnId.toString()));
      const filteredRows = [...codeRows, ...descriptionRows];
      // Merge rows with the same rowId.
      const mergedRows = filteredRows
        .reduce((acc, row) => {
          const existing = acc.find((item) => item.rowId === row.rowId);
          let newValueKey;
          let columnKey;

          if (codeColumnId.includes(row.columnId.toString())) {
            newValueKey = 'code';
            columnKey = 'codeId';
          } else if (descriptionColumnId.includes(row.columnId.toString())) {
            newValueKey = 'description';
            columnKey = 'descriptionId';
          } else {
            newValueKey = 'value';
          }

          if (existing) {
            // If a row with the same rowId already exists, add the new field and columnId.
            existing[newValueKey] = row.value || ''; // Ensure a default value for code or description.
            if (columnKey) {
              existing[columnKey] = row.columnId;
            }
          } else {
            // Otherwise, add a new entry.
            acc.push({
              ...row,
              [newValueKey]: row.value || '', // Ensure a default value for code or description.
              ...(columnKey ? { [columnKey]: row.columnId } : {}),
            });
          }

          return acc;
        }, [])
        .map((item) => ({
          ...item,
          code: item.code || '', // Ensure `code` is always a string.
          description: item.description || '', // Ensure `description` is always a string.
        }));
      setState((prev) => ({
        ...prev,
        CareLibraryList: mergedRows.filter((item) => hasSearchableText(item.code) || hasSearchableText(item.description)),
      }));
    }
  }, [careLibrariesData, userState.tabId]);

  useEffect(() => {
    const dropdownList =
      productList?.length > 0
        ? productList?.map((item) => {
            let obj = {};
            obj = {
              label: `${item.number} - ${item.description}`,
              // eslint-disable-next-line no-underscore-dangle
              value: item._id,
            };
            return obj;
          })
        : [];
    setState((prev) => ({
      ...prev,
      productList: dropdownList,
    }));
  }, [productList]);

  const selectDropDown = async (e) => {
    setSelectDrop({ value: e.value, label: e.label });
    if (e.value === 'Product') {
      await dispatch(careLabelAction.fetchCareProduct(devOrderId));
    } else if (e.value === 'Group') {
      await dispatch(careLabelAction.getProductCareGroup(devOrderId));
    }
  };

  const selectProductDropDown = async (e) => {
    setState((prev) => ({
      ...prev,
      selectProductList: e,
    }));
    await dispatch(careLabelAction.fetchProductCareData({ devOrderId: e.value, tabId: userState.tabId }));
  };

  const onAutoSuggestion = async (suggestion) => {
    if (suggestion?.length > 0 && careLibrariesData?.rows?.length > 0) {
      typeaheadConfig.current.clear();
      let data;
      if (userState.addNewRow) {
        // Clone tabDetails and filter out entries without rowId
        const copysTabData = cloneDeep(CareLabelstate?.CareLabelList?.[findIndex]?.tabDetails);
        const filteredData = copysTabData?.filter((item) => item.rowId);

        // Create an updated CareLabelList with filtered tabDetails
        const updatedCareLabelList = CareLabelstate.CareLabelList.map((item, index) => {
          if (index === findIndex) {
            return {
              ...item,
              tabDetails: filteredData, // Replace with filtered data
            };
          }
          return item;
        });
        console.log('updatedCareLabelList.updatedCareLabelList', updatedCareLabelList);
        // Update state
        setCareLabelState((prevState) => ({
          ...prevState,
          CareLabelList: updatedCareLabelList,
        }));
        data = cloneDeep(updatedCareLabelList);
        setUserState((prevState) => ({
          ...prevState,
          editingRowId: '',
          addNewRow: false,
        }));
      } else {
        data = cloneDeep(CareLabelstate?.CareLabelList);
      }
      const isRowIdPresent = data?.some((tab) => tab?.tabDetails?.some((detail) => detail?.rowId === suggestion[0]?.rowId));
      if (isRowIdPresent) {
        toasterMsg(t('CARE_LABELS.GROUP.ADD_EDIT.SELECTED_CARE_LABEL_COMPONENT'), 'danger');
      } else {
        const tabRows = careLibrariesData?.rows?.filter((row) => row?.rowId === suggestion[0]?.rowId && row.tabId === suggestion[0]?.tabId);
        console.log('suggestion[0]', suggestion[0]);
        console.log('tabRows[0]', tabRows);
        console.log('careLibrariesData?.rows?[0]', careLibrariesData?.rows);
        data = data?.map((tab) => {
          // Check if the tabId matches the selected suggestion
          if (tab.tabId === suggestion[0]?.tabId && tabRows.length > 0) {
            return {
              ...tab,
              tabDetails: [...(tab.tabDetails || []), ...tabRows], // Push the suggestion into tabDetails
            };
          }
          return tab;
        });
        setCareLabelState((prevState) => ({
          ...prevState,
          CareLabelList: data,
        }));
        const res = await dispatch(careLabelAction.importCareLabel({ devOrderId, tabId: suggestion[0]?.tabId, tabs: data, tabRows }, t));
        if (res) {
          await dispatch(careLabelAction.fetchCareInstructionProduct({ devOrderId }));
        }
      }
    }
  };

  const onAddCareLabelGroup = async (selected) => {
    newtypeaheadConfig.current.clear();
    const reqObj = {
      devOrderId,
      tabId: userState.tabId,
      groupId: selected[0]._id,
    };
    const res = await dispatch(careLabelAction.importCareGroup(reqObj, t));
    if (res?.code === 200) {
      await dispatch(careLabelAction.fetchCareInstructionProduct({ devOrderId }));
    }
  };

  const hideShowDragDrop = () => {
    setState((prev) => ({
      ...prev,
      showDragDrop: !state.showDragDrop,
    }));
  };

  const handleSaveSequence = async (_e, sequenceList) => {
    const param = {
      devOrderId,
    };
    // Update CareLibraryList with sequence values from sequenceList
    const updatedCareLibraryList = CareLabelList?.[findIndex]?.tabDetails.map((item) => {
      // Find the matching sequence in sequenceList
      const matchingSequence = sequenceList?.sequence?.find((seq) => seq.rowId === item.rowId);

      // If a match is found, update the sequence value
      return matchingSequence ? { ...item, sequence: matchingSequence.sequence, rowIndex: matchingSequence.sequence } : item;
    });
    const updatedCareLabelList = CareLabelList.map((item, index) => {
      if (index === findIndex) {
        return {
          ...item,
          tabDetails: updatedCareLibraryList, // Push the new tabDetails
        };
      }
      return item;
    });
    setCareLabelState((prevState) => ({
      ...prevState,
      CareLabelList: updatedCareLabelList,
    }));
    const payload = {
      devOrderId,
      tabs: updatedCareLabelList,
    };

    const res = await dispatch(careLabelAction.rearrangeCareLabel(payload, t));
    if (res) {
      await dispatch(careLabelAction.fetchCareInstructionProduct(param));
      hideShowDragDrop();
    }
  };
  // Create a map to store entries by rowId
  const itemMap = {};
  CareLabelList?.[findIndex]?.tabDetails?.forEach((prop) => {
    if (prop?.columnId === codeColumn || prop.columnId === typeColumn) {
      // Check if the rowId already exists in the map
      if (!itemMap[prop.rowId]) {
        itemMap[prop.rowId] = { rowId: prop.rowId }; // Initialize a new entry with the rowId
      }

      // Add the value to the appropriate key
      if (prop.columnId === codeColumn) {
        itemMap[prop.rowId].code = prop.value;
      } else if (prop.columnId === typeColumn) {
        itemMap[prop.rowId].careType = prop.value;
      }
      itemMap[prop.rowId].tabId = prop.tabId;
    }
  });
  const itemsList = Object.values(itemMap);

  return (
    <div className="mt-24 product-care-list pt-list">
      <div className="filter-container">
        <div style={{ zIndex: 4, width: '75px' }}>
          <Select
            options={options}
            components={{ Option: CustomOption }}
            styles={selectLibraryStyle}
            placeholder={placeHolder()}
            //  value={selectDrop?.value}
            onChange={(e) => selectDropDown(e)}
            isDisabled={isProductArchived}
            classNamePrefix="common-select"
            defaultValue={{ value: 'Library', label: <i className="icon-Library-1 library-icon-css f-24" />, title: t('TAKING_DATES.LIBRARYS') }}
            isSearchable={false}
          />
        </div>
        <form style={{ width: 'auto' }}>
          {selectDrop?.value === 'Library' && (
            <form>
              <Typeahead
                promptText={t('PROGRESS_INDICATOR.NO_PRODUCT')}
                className="library-input"
                placeholder={t('TAKING_DATES.SEARCH_IMPORTS')}
                labelKey="code"
                filterBy={CARE_LIBRARY_SEARCH_FIELDS}
                options={state.CareLibraryList}
                minLength={1}
                disabled={(!CareAddEditAccess, isProductArchived)}
                onChange={(selected) => onAutoSuggestion(selected)}
                id="typeheadWithLibrary"
                renderMenuItemChildren={(option) => (
                  <div className="d-flex" key={option?._id}>
                    <div className="list-text">
                      {option?.code}-{option?.description}
                    </div>
                  </div>
                )}
                ref={typeaheadConfig}
              />
            </form>
          )}
          {console.log('state?.productList', state?.productList, state?.selectProductList)}

          {selectDrop?.value === 'Group' && (
            <form>
              <Typeahead
                promptText={t('PROGRESS_INDICATOR.NO_PRODUCT')}
                placeholder={t('TAKING_DATES.SEARCH_CARE_LABEL_PRODUCT')}
                labelKey="name"
                className="library-input"
                options={careGroupList}
                minLength={1}
                disabled={!CareAddEditAccess}
                onChange={(selected) => onAddCareLabelGroup(selected)}
                id="typeheadWithGroup"
                useCache={false}
                renderMenuItemChildren={(option) => (
                  <div className="d-flex" key={option?._id}>
                    <div className="list-text">{option?.name}</div>
                  </div>
                )}
                ref={newtypeaheadConfig}
              />
            </form>
          )}
          {selectDrop?.value === 'Product' && (
            <div className="d-flex w-500px">
              <div className="w-200px product-select mb-0">
                <Select
                  options={state?.productList}
                  menuPosition="fixed"
                  menuPlacement="top"
                  isClearable
                  placeholder={t('DEVLOPMENT_ORDER.BOM.CARE_LABEL_TAB.SELECT')}
                  styles={selectNewStyle}
                  value={state?.selectProductList}
                  className="common-select"
                  classNamePrefix="common-select"
                  onChange={(select) => selectProductDropDown(select)}
                />
              </div>

              <form>
                <Typeahead
                  promptText={t('TAKING_DATES.NO_PRODUCTS_FOUND')}
                  placeholder={`${t('CARE-DESCRIPTION.SEARCH_IMPORT_CARE_LABEL')} ${t('CARE-DESCRIPTION.FROM_ANOTHER_PRODUCT')}`}
                  labelKey="code"
                  filterBy={CARE_LIBRARY_SEARCH_FIELDS}
                  className="library-input"
                  options={state.CareLibraryList}
                  minLength={1}
                  onChange={(selected) => onAutoSuggestion(selected)}
                  id="typeheadWithProduct"
                  renderMenuItemChildren={(option) => (
                    <div className="d-flex" key={option?._id}>
                      <div className="list-text">
                        {option?.code}-{option?.description}
                      </div>
                    </div>
                  )}
                  disabled={!CareAddEditAccess}
                  ref={typeaheadConfig}
                />
              </form>
            </div>
          )}
        </form>
        {CareAddEditAccess && (
          <CustomTooltip placement="top" tooltipText={t('SPEC_TEMP_LIBRARY.ADD')}>
            <button
              type="button"
              aria-label="add"
              className="btn common-btn btn-outline mr-0"
              onClick={() => handleAddCareLabel()}
              disabled={isProductArchived}
            >
              <i className="icon-Add text-grey f-24" />
            </button>
          </CustomTooltip>
        )}
        {CareAddEditAccess && (
          <CustomTooltip placement="top" tooltipText={t('COMMON_LABEL.REARRANGE')}>
            <button type="button" aria-label="add" className="btn common-btn btn-outline mr-0" onClick={hideShowDragDrop} disabled={isProductArchived}>
              <i className="icon-Rearrange text-grey  f-24" />
            </button>
          </CustomTooltip>
        )}
        {CareDeleteAccess && (
          <CustomTooltip placement="top" tooltipText={t('SPEC_TEMP_LIBRARY.DELETE')}>
            <button
              type="button"
              aria-label="delete"
              onClick={() => handleDeleteMultiple()}
              className="btn  common-btn btn-outline filter-btn filter-button_delete"
              disabled={(!userState?.selectedIdArray?.length, isProductArchived)}
            >
              <i className="icon-Delete text-grey f-24" />
            </button>
          </CustomTooltip>
        )}
      </div>
      {state?.showDragDrop && (
        <DragDropSort
          devOrderId={devOrderId}
          itemList={itemsList}
          isShow={state.showDragDrop}
          isToggle={hideShowDragDrop}
          saveSequence={handleSaveSequence}
          fromComp="careLabelProducts"
        />
      )}
    </div>
  );
}

export default ProductCareImport;
